import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Upload, Pause, Play, XCircle, Trash2, RefreshCw,
  Loader2, AlertCircle, ChevronUp, ChevronDown,
  Wifi, WifiOff, Youtube, Radio, Lock, AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { schedulesApi, facilitiesApi, queryKeys, StreamSchedule, BlackoutDate } from '@/lib/api';
import { fmtInTz, timeRemaining, naturalCompare } from '@/lib/utils';
import { useActiveFacilityId } from '@/hooks/useActiveFacilityId';
import { useAuth } from '@/store/auth';
import ScheduleFormModal from '@/components/schedules/ScheduleFormModal';
import CsvImportModal from '@/components/schedules/CsvImportModal';
import ScheduleRulesPanel from '@/components/schedules/ScheduleRulesPanel';
import LiveStreamsTab from '@/components/LiveStreamsTab';
import GenericRecurringEditor from '@/components/GenericRecurringEditor';
import EventModePanel from '@/components/EventModePanel';
import AdHocStreamsTab from '@/components/AdHocStreamsTab';
import { eventsApi } from '@/lib/api';

// ─── Agent Types ──────────────────────────────────────────────────────────────
export interface AgentSessionStatus {
  sheetName: string;
  state: string;
  obsConnected: boolean;
  broadcastId: string | null;
  broadcastStatus: string | null;
  lastError: string | null;
  isUnscheduled: boolean;
  startedRemotelyBy?: string | null;
  /** On air with a game of a CurlOps-linked event, which only CurlOps stops. */
  curlOpsControlled?: boolean;
}

export interface AgentHostStatus {
  host: { id: number; hostLabel: string; pollIntervalSeconds: number };
  lastHeartbeat: { seenAt: string } | null;
  isOnline: boolean;
  hostState: string | null;
  sessions: AgentSessionStatus[];
  agentVersion: string | null;
  latestAgentVersion: string | null;
  /** NDI Discovery Server monitor (agent 1.0.37+); null/absent on older agents. */
  ndiDiscovery?: {
    expected: boolean;
    state: 'ok' | 'restarting' | 'faulted' | 'not_expected' | 'unknown';
    running: boolean;
    restartsToday: number;
    lastStoppedAt: string | null;
    lastRestartAt: string | null;
    lastError: string | null;
  } | null;
}

/** Extract the sheet letter from agent sheetName ("Sheet A" → "A", "Sheet 1" → "1") */
function sheetLetterFromName(name: string): string {
  return name.replace(/^Sheet\s+/i, '').trim();
}

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status, isPaused, skipRule }: {
  status: StreamSchedule['status']; isPaused: boolean; skipRule?: string | null | undefined;
}) {
  const base = 'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold';
  // A Skip rule kept this game off air (upcoming and paused, or in the Archive
  // afterwards). That's the club's own setting, not a fault: say so plainly.
  if (skipRule && ((status === 'pending' && isPaused) || status === 'skipped')) {
    return (
      <span className={`${base} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200`}
        title={`Your Schedule Rule "${skipRule}" skips games with this in their name. Resume streams this one anyway.`}>
        skipped by rule
      </span>
    );
  }
  const map: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    streaming: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    completed: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    skipped: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  };
  const fallback = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
  return (
    <span className={`${base} ${map[status] ?? fallback}`}>
      {isPaused ? <span className="mr-1 text-gray-400">(paused)</span> : null}
      {/* 'skipped' is set when a game's time passed without it streaming. */}
      {status === 'skipped' ? 'missed' : status}
    </span>
  );
}

// ─── Live Confirm Badge ───────────────────────────────────────────────────────
// Shown next to the status badge for rows where DB status is 'streaming'.
// Compares the DB status against the latest agent heartbeat session data.
function LiveConfirmBadge({ sheetId, liveMap }: {
  sheetId: string;
  liveMap: Map<string, AgentSessionStatus>;
}) {
  const session = liveMap.get(sheetId);
  if (!session) {
    // Agent data unavailable — no indicator
    return null;
  }
  if (session.state === 'STREAMING' && session.obsConnected) {
    return (
      <span title="Agent confirmed: OBS streaming" className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-300">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-green-500" />
        </span>
        Live
      </span>
    );
  }
  if (session.state === 'STREAMING' && !session.obsConnected) {
    return (
      <span title="Streaming but OBS is disconnected" className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        <WifiOff className="h-2.5 w-2.5" />
        OBS lost
      </span>
    );
  }
  if (session.state === 'ERROR') {
    return (
      <span title={session.lastError ?? 'Agent error'} className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/40 dark:text-red-300">
        <AlertCircle className="h-2.5 w-2.5" />
        Agent error
      </span>
    );
  }
  // DB says streaming but agent disagrees (IDLE, STOPPING, etc.)
  return (
    <span title={`Agent reports: ${session.state}`} className="ml-1.5 inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-700 dark:text-gray-400">
      {session.state}
    </span>
  );
}

// ─── Schedules Tab ────────────────────────────────────────────────────────────
function SchedulesTab({ facilityId, liveMap, facilityTimezone, scheduleSource, googleSheetsUrl, ccmApiUrl, leagueAppsConfigured, curlOpsConfigured, streamingPaused, lastSheetsSync, sheetNames, pausedSheets, scheduleWindowDays, lastSyncIssues, archive }: {
  facilityId: string | null;
  liveMap: Map<string, AgentSessionStatus>;
  facilityTimezone?: string;
  scheduleSource?: 'manual' | 'google_sheets' | 'ccm_api' | 'leagueapps_api' | 'generic_recurring' | 'curlops_api';
  googleSheetsUrl?: string | null;
  ccmApiUrl?: string | null;
  leagueAppsConfigured?: boolean;
  curlOpsConfigured?: boolean;
  scheduleWindowDays?: number;
  lastSyncIssues?: string | null;
  streamingPaused?: boolean;
  lastSheetsSync?: string | null;
  sheetNames?: string | null;
  pausedSheets?: string | null;
  // Archive mode: show finished/terminal streams (completed, cancelled, skipped) as a
  // read-only history view. Active mode (default): only pending + streaming.
  archive?: boolean;
}) {
  const queryClient = useQueryClient();

  // In archive mode the default set is the terminal statuses; otherwise the active ones.
  // An empty status filter resolves to this default.
  const DEFAULT_STATUSES = archive ? 'completed,cancelled,skipped' : 'pending,streaming';

  const [filterSheet, setFilterSheet] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [sortField, setSortField] = useState<'scheduledStart' | 'eventName' | 'sheetId'>('scheduledStart');
  // Archive defaults to most-recent-first; active list to soonest-first.
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(archive ? 'desc' : 'asc');
  const [showCreate, setShowCreate] = useState(false);
  const [editSchedule, setEditSchedule] = useState<StreamSchedule | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // None of the schedule sources are two-way: an edit or delete made here is
  // overwritten (or resurrected) on the next sync, so the source owns these
  // games. This applies to ADMINS TOO — an admin edit is silently reverted by
  // the next sync, and an admin delete now writes a permanent tombstone, which
  // is a bigger consequence than the same click used to have. Admins keep the
  // delete as a deliberate, confirmed override rather than a normal button.
  const sourceManaged = !!scheduleSource && scheduleSource !== 'manual';
  // Which sources this page can actually pull on demand. generic_recurring has
  // no remote feed — its games come from the weekly template editor below.
  const canFetchSource =
    (scheduleSource === 'google_sheets' && !!googleSheetsUrl) ||
    (scheduleSource === 'ccm_api' && !!ccmApiUrl) ||
    (scheduleSource === 'leagueapps_api' && !!leagueAppsConfigured) ||
    (scheduleSource === 'curlops_api' && !!curlOpsConfigured);
  const horizonLabel = scheduleWindowDays
    ? new Date(Date.now() + scheduleWindowDays * 86400000).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    : null;
  const sourceLabel =
    scheduleSource === 'google_sheets' ? 'your Google Sheet'
      : scheduleSource === 'ccm_api' ? 'CurlingClubManager'
      : scheduleSource === 'leagueapps_api' ? 'LeagueApps'
      : scheduleSource === 'curlops_api' ? 'CurlOps'
      : scheduleSource === 'generic_recurring' ? 'the recurring schedule template'
      : 'your schedule source';
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [showRecurring, setShowRecurring] = useState(true);

  const queryParams = {
    ...(facilityId ? { facilityId } : {}),
    ...(filterSheet ? { sheetId: filterSheet } : {}),
    status: filterStatus || DEFAULT_STATUSES,
    ...(filterFrom ? { from: new Date(filterFrom).toISOString() } : {}),
    ...(filterTo ? { to: new Date(filterTo).toISOString() } : {}),
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.schedules.list(queryParams),
    queryFn: () => schedulesApi.list(queryParams),
  });

  const pauseMutation = useMutation({
    mutationFn: (id: number) => schedulesApi.pause(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all }),
  });
  const resumeMutation = useMutation({
    mutationFn: (id: number) => schedulesApi.resume(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all }),
  });
  const cancelMutation = useMutation({
    mutationFn: (id: number) => schedulesApi.cancel(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: number) => schedulesApi.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all }),
  });

  const schedules: StreamSchedule[] = data?.schedules || [];

  const sorted = [...schedules].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'scheduledStart') {
      cmp = new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime();
    } else if (sortField === 'eventName') {
      cmp = a.eventName.localeCompare(b.eventName);
    } else if (sortField === 'sheetId') {
      // Natural, not lexicographic: localeCompare puts sheet 10 before sheet 4,
      // which is wrong everywhere and very wrong at the clubs with 10 sheets.
      cmp = naturalCompare(a.sheetId, b.sheetId);
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp className="inline h-3 w-3" /> : <ChevronDown className="inline h-3 w-3" />;
  };

  const activeFacilityId = facilityId || '';

  const pauseAllMutation = useMutation({
    mutationFn: (paused: boolean) => schedulesApi.pauseAll(activeFacilityId, paused),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['facilities-list'] }),
  });

  const wipeFutureMutation = useMutation({
    mutationFn: () => schedulesApi.wipeFuture(activeFacilityId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all });
      alert(`Removed ${res?.deleted ?? 0} upcoming event(s). Streaming and completed games were kept. Re-import or sync to repopulate.`);
    },
  });

  const wipeFuture = () => {
    if (confirm('Wipe ALL future events for this facility?\n\nThis permanently removes every upcoming (not-yet-started) game so you can re-import a clean schedule. Live and completed games are NOT affected.\n\nContinue?')) {
      wipeFutureMutation.mutate();
    }
  };

  const togglePauseAll = () => {
    if (streamingPaused) {
      pauseAllMutation.mutate(false);
    } else {
      if (confirm('Pause ALL streaming for this facility?\n\nWhile paused, the agent will not auto-start any sessions regardless of schedule. Use this for emergencies or facility-wide outages.')) {
        pauseAllMutation.mutate(true);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Pause-all banner */}
      {!archive && streamingPaused && (
        <div className="flex items-start gap-3 rounded-lg border-2 border-red-300 bg-red-50 px-4 py-3 dark:border-red-700 dark:bg-red-900/20">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-red-900 dark:text-red-200">
              All streaming is paused for this facility.
            </p>
            <p className="mt-0.5 text-xs text-red-700 dark:text-red-300">
              The agent will not auto-start any session until Pause All is turned off. Schedule entries are preserved.
            </p>
          </div>
          <button
            onClick={togglePauseAll}
            disabled={pauseAllMutation.isPending}
            className="shrink-0 rounded-md border border-red-400 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-100 disabled:opacity-50 dark:border-red-700 dark:bg-red-900/40 dark:text-red-200 dark:hover:bg-red-900/60"
          >
            Resume all
          </button>
        </div>
      )}

      {/* Schedule Rules — game lengths by title, for the sources that do not
          always send one. Other sources carry explicit ends, so it is hidden. */}
      {!archive && facilityId &&
        (scheduleSource === 'ccm_api' || scheduleSource === 'leagueapps_api' || scheduleSource === 'curlops_api' || scheduleSource === 'google_sheets') && (
        <ScheduleRulesPanel facilityId={facilityId} {...(facilityTimezone ? { facilityTimezone } : {})} />
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Sheet</label>
          <select
            value={filterSheet}
            onChange={e => setFilterSheet(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">All Sheets</option>
            {['A', 'B', 'C', 'D', 'E', 'F'].map(s => (
              <option key={s} value={s}>Sheet {s}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Status</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="">All Statuses</option>
            {archive ? (
              <>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="skipped">Missed</option>
              </>
            ) : (
              <>
                <option value="pending">Pending</option>
                <option value="streaming">Streaming</option>
              </>
            )}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">From</label>
          <input
            type="date"
            value={filterFrom}
            onChange={e => setFilterFrom(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-600 dark:text-gray-400">To</label>
          <input
            type="date"
            value={filterTo}
            onChange={e => setFilterTo(e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
          {/* A source-managed facility with no fetchable source (recurring
              templates) would open the CSV paste box, and the API 409s that
              path — so do not offer it. Sources that CAN fetch still show
              their Sync button. */}
          {!archive && !(sourceManaged && !canFetchSource) && (
            <div className="flex flex-col items-end gap-0.5">
              <Button variant="outline" size="sm" onClick={() => setShowCsvImport(true)}>
                <Upload className="mr-1 h-4 w-4" />
                {scheduleSource === 'ccm_api' ? 'Sync from CCM'
                  : scheduleSource === 'google_sheets' ? 'Sync from Sheet'
                  : scheduleSource === 'leagueapps_api' ? 'Sync from LeagueApps'
                  : scheduleSource === 'curlops_api' ? 'Sync from CurlOps'
                  : 'Import CSV'}
              </Button>
              {(scheduleSource === 'google_sheets' || scheduleSource === 'ccm_api' || scheduleSource === 'leagueapps_api' || scheduleSource === 'curlops_api') && lastSheetsSync && (
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  Last synced {new Date(lastSheetsSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>
          )}
          {!archive && !sourceManaged && (
            <Button size="sm" onClick={() => setShowCreate(true)}>
              <Plus className="mr-1 h-4 w-4" />
              New Schedule
            </Button>
          )}
          {!archive && !streamingPaused && (
            <Button
              size="sm"
              variant="outline"
              onClick={togglePauseAll}
              disabled={pauseAllMutation.isPending}
              className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              <AlertCircle className="mr-1 h-4 w-4" />
              Pause All
            </Button>
          )}
          {!archive && (
            <Button
              size="sm"
              variant="outline"
              onClick={wipeFuture}
              disabled={wipeFutureMutation.isPending}
              className="border-red-400 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/20"
              title="Remove all upcoming events for a clean re-import (live and completed games are kept)"
            >
              <Trash2 className="mr-1 h-4 w-4" />
              {wipeFutureMutation.isPending ? 'Wiping…' : 'Wipe Future'}
            </Button>
          )}
        </div>
      </div>

      {/* What the last sync could not read, and games it had to interpret. These
          used to be a number in a server log: a club whose feed had one bad time
          simply never saw that game and had no way to find out. */}
      {!archive && (() => {
        let allIssues: Array<{ kind?: string; row?: number; sheet?: string | null; event?: string | null; reason?: string }> = [];
        try { allIssues = lastSyncIssues ? JSON.parse(lastSyncIssues) : []; } catch { allIssues = []; }
        // CSM's own preparation of the sheet failing (kind 'prep') is not
        // something the club can fix at the source, so it gets its own box.
        const prepIssues = allIssues.filter(i => i.kind === 'prep');
        const issues = allIssues.filter(i => i.kind !== 'prep');
        const flagged = (schedules ?? []).filter(s => s.syncWarning);
        if (allIssues.length === 0 && flagged.length === 0) return null;
        // Two different things, deliberately not in one box.
        //
        // A row we could not READ is a fault, it is actionable, and it stays
        // amber. A row we imported but had to ADJUST is neither: the club is
        // told because a shortened stream is a real consequence, but nothing
        // is broken and there may be nothing to fix. Dakota had 9 of the
        // second kind — the same three Wednesday draws every week, since
        // their Triples draw nominally runs past the start of Doubles — and
        // the shared banner told them their source "needs a look" and that it
        // would "clear on the next sync". Neither was true, and it would have
        // said so every week forever.
        return (
          <div className="space-y-2">
            {prepIssues.map((it, i) => (
              <div key={`prep-${i}`} className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
                <span className="font-semibold">Your latest schedule isn’t coming through.</span>{' '}{it.reason}
              </div>
            ))}
            {issues.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                <span className="font-semibold">Your schedule source needs a look.</span>
                <div className="mt-1">
                  <span className="font-medium">{issues.length} row{issues.length === 1 ? '' : 's'} need{issues.length === 1 ? 's' : ''} attention at the source:</span>
                  <ul className="ml-4 mt-0.5 list-disc">
                    {issues.slice(0, 5).map((it, i) => (
                      <li key={i}>
                        {it.event ? <strong>{it.event}</strong> : 'Row'}{it.sheet ? ` on sheet ${it.sheet}` : ''}
                        {it.row ? ` (row ${it.row})` : ''} — {it.reason}
                      </li>
                    ))}
                    {issues.length > 5 && <li>…and {issues.length - 5} more</li>}
                  </ul>
                </div>
                <div className="mt-1 text-amber-800 dark:text-amber-300">
                  Fix these at the source and they'll clear on the next sync.
                </div>
              </div>
            )}
            {flagged.length > 0 && (
              <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
                <span className="font-medium text-gray-900 dark:text-gray-100">{flagged.length} game{flagged.length === 1 ? ' is' : 's are'} tight against the game before it on the same sheet.</span>{' '}
                Look for the <AlertTriangle className="inline h-3 w-3" /> in the list below — each one gives the times.
                <div className="mt-1 text-gray-600 dark:text-gray-400">
                  CSM normally brings a stream up about 5 minutes before the draw time. Where the previous game on
                  that sheet ends less than 5 minutes earlier, the later stream comes up as soon as the sheet frees
                  instead — so it gets less warm-up, but it still streams. Your times are left exactly as your
                  source has them; nothing here has been changed.
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* The schedule source owns these games. Say so once, plainly, instead of
          letting a club discover it by having their edit silently undone. */}
      {!archive && sourceManaged && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
          <span className="font-medium">This schedule is managed in {sourceLabel}.</span>{' '}
          Add, change or remove games there and CSM follows automatically — changes made here
          would be overwritten on the next sync.
          {scheduleSource === 'google_sheets' && ' To stop a single game from streaming, mark it as not streamed in the sheet.'}
          {' '}For a one-off that isn't in {sourceLabel}, use an Event or an ad-hoc stream.
          {isAdmin && (
            <> <span className="font-medium">Admin:</span> the lock on each row is an override —
            it excludes a game permanently rather than changing {sourceLabel}.</>
          )}
          {scheduleWindowDays ? (
            <div className="mt-1 border-t border-blue-200 pt-1 dark:border-blue-900">
              Games are imported about <span className="font-medium">{scheduleWindowDays} days ahead</span>
              {horizonLabel ? <> — currently through <span className="font-medium">{horizonLabel}</span></> : null}.
              Anything later is still in {sourceLabel} and appears here automatically as its date comes closer,
              so a game you add for next month won't be listed yet.
              {/* Name the button actually on screen for this source — it used to
                  say "Sync from Sheet" to CCM and LeagueApps clubs too. The
                  recurring template has no button, and syncs from its own editor. */}
              {canFetchSource && (
                <>
                  {' '}Syncs run automatically; use
                  <span className="font-medium">
                    {' '}{scheduleSource === 'ccm_api' ? 'Sync from CCM'
                      : scheduleSource === 'leagueapps_api' ? 'Sync from LeagueApps'
                      : scheduleSource === 'curlops_api' ? 'Sync from CurlOps'
                      : 'Sync from Sheet'}
                  </span> to pull changes immediately.
                </>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Weekly recurring setup — only for facilities on the generic_recurring source.
          Club managers manage their own weekly slots here (endpoints are token-scoped). */}
      {!archive && scheduleSource === 'generic_recurring' && facilityId && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/40 dark:border-blue-900/50 dark:bg-blue-950/20">
          <button
            onClick={() => setShowRecurring(v => !v)}
            className="flex w-full items-center justify-between px-4 py-3 text-left"
          >
            <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
              <RefreshCw className="h-4 w-4 text-blue-500" />
              Weekly Recurring Setup
              <span className="font-normal text-gray-500 dark:text-gray-400">
                — define your weekly draw times, then generate the schedule 30 days out
              </span>
            </span>
            {showRecurring
              ? <ChevronUp className="h-4 w-4 shrink-0 text-gray-400" />
              : <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />}
          </button>
          {showRecurring && (
            <div className="px-4 pb-4">
              <GenericRecurringEditor facilityId={facilityId} {...(sheetNames ? { sheetNames } : {})} />
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          Failed to load schedules
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th
                  className="cursor-pointer px-4 py-3 text-left font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  onClick={() => toggleSort('eventName')}
                >
                  Event Name <SortIcon field="eventName" />
                </th>
                <th
                  className="cursor-pointer px-4 py-3 text-left font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  onClick={() => toggleSort('sheetId')}
                >
                  Sheet <SortIcon field="sheetId" />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Teams</th>
                <th
                  className="cursor-pointer px-4 py-3 text-left font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  onClick={() => toggleSort('scheduledStart')}
                >
                  Start <SortIcon field="scheduledStart" />
                  {facilityTimezone && (
                    <span className="ml-1 font-normal normal-case text-gray-400 dark:text-gray-500">({facilityTimezone})</span>
                  )}
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">End</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    {archive ? 'No completed streams yet' : 'No schedules found'}
                  </td>
                </tr>
              ) : (
                sorted.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-3 font-medium">
                      {s.eventName}
                      {s.syncWarning && (
                        <span title={s.syncWarning} className="ml-1 inline-flex align-text-bottom text-amber-600 dark:text-amber-500">
                          <AlertTriangle className="h-3.5 w-3.5" />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                        Sheet {s.sheetId}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {s.team1Name && s.team2Name
                        ? `${s.team1Name} vs ${s.team2Name}`
                        : s.team1Name || s.team2Name || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {fmtInTz(s.scheduledStart, facilityTimezone)}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {fmtInTz(s.scheduledEnd, facilityTimezone)}
                      {s.endSource === 'default' && (
                        <span
                          title="Your source gives no length for this game, so CSM used your site's default length. A Schedule Rule can set the real length."
                          className="ml-1 rounded bg-gray-100 px-1 text-[10px] font-medium uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                        >
                          est.
                        </span>
                      )}
                      {s.endSource === 'rule' && (
                        <span
                          title="Length set by one of your Schedule Rules."
                          className="ml-1 rounded bg-blue-50 px-1 text-[10px] font-medium uppercase text-blue-600 dark:bg-blue-950/40 dark:text-blue-300"
                        >
                          rule
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge status={s.status} isPaused={s.isPaused} skipRule={s.skipRule} />
                        {s.skipRule && s.status === 'pending' && !s.isPaused && (
                          <span className="ml-1 text-[11px] text-gray-500 dark:text-gray-400"
                            title={`Your Schedule Rule "${s.skipRule}" would skip this game, but it was resumed, so it will stream.`}>
                            (streaming despite skip rule)
                          </span>
                        )}
                        {s.status === 'streaming' && (
                          <LiveConfirmBadge sheetId={s.sheetId} liveMap={liveMap} />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {/* Pause/Resume */}
                        {s.status === 'pending' || s.status === 'streaming' ? (
                          s.isPaused ? (
                            <button
                              title="Resume"
                              onClick={() => resumeMutation.mutate(s.id)}
                              disabled={resumeMutation.isPending}
                              className="rounded p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                            >
                              <Play className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              title="Pause"
                              onClick={() => pauseMutation.mutate(s.id)}
                              disabled={pauseAllMutation.isPending}
                              className="rounded p-1 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20"
                            >
                              <Pause className="h-4 w-4" />
                            </button>
                          )
                        ) : null}
                        {/* Cancel */}
                        {(s.status === 'pending' || s.status === 'streaming') && (
                          <button
                            title="Cancel"
                            onClick={() => {
                              if (confirm('Cancel this schedule?')) cancelMutation.mutate(s.id);
                            }}
                            disabled={cancelMutation.isPending}
                            className="rounded p-1 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                          >
                            <XCircle className="h-4 w-4" />
                          </button>
                        )}
                        {/* Edit — not offered in the archive/history view, nor for
                            games owned by a schedule source (see sourceManaged) */}
                        {!archive && !(sourceManaged && !s.eventId) && (
                          <button
                            title="Edit"
                            onClick={() => setEditSchedule(s)}
                            className="rounded p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        )}
                        {/* Delete */}
                        {sourceManaged && !s.eventId ? (
                          isAdmin ? (
                            <button
                              title={`Managed by ${sourceLabel}. Admin override — permanently excludes this game from CSM.`}
                              onClick={() => {
                                if (confirm(
                                  `This game is managed in ${sourceLabel}.

` +
                                  `Deleting it here does NOT change the source — it records a permanent ` +
                                  `exclusion, so the game will not come back on the next sync even though ` +
                                  `it is still listed there.

` +
                                  `The normal way to stop a game streaming is to change it in ${sourceLabel}.

` +
                                  `Override and exclude "${s.eventName}" anyway?`
                                )) deleteMutation.mutate(s.id);
                              }}
                              disabled={deleteMutation.isPending}
                              className="rounded p-1 text-amber-600 hover:bg-amber-50 dark:text-amber-500 dark:hover:bg-amber-900/20"
                            >
                              <Lock className="h-4 w-4" />
                            </button>
                          ) : (
                            <span
                              title={`Managed by ${sourceLabel} — remove it there and CSM will follow`}
                              className="cursor-not-allowed rounded p-1 text-gray-300 dark:text-gray-600"
                            >
                              <Lock className="h-4 w-4" />
                            </span>
                          )
                        ) : (
                          <button
                            title="Delete"
                            onClick={() => {
                              if (confirm('Delete this schedule?')) deleteMutation.mutate(s.id);
                            }}
                            disabled={deleteMutation.isPending}
                            className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <ScheduleFormModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        facilityId={activeFacilityId}
        {...(facilityTimezone ? { facilityTimezone } : {})}
        {...(sheetNames ? { sheetNames } : {})}
        {...(pausedSheets ? { pausedSheets } : {})}
      />
      {editSchedule && (
        <ScheduleFormModal
          open={!!editSchedule}
          onClose={() => setEditSchedule(null)}
          facilityId={activeFacilityId}
          {...(facilityTimezone ? { facilityTimezone } : {})}
          {...(sheetNames ? { sheetNames } : {})}
          {...(pausedSheets ? { pausedSheets } : {})}
          schedule={editSchedule}
        />
      )}
      <CsvImportModal
        open={showCsvImport}
        onClose={() => setShowCsvImport(false)}
        facilityId={activeFacilityId}
        {...(facilityTimezone ? { facilityTimezone } : {})}
        {...(scheduleSource ? { scheduleSource } : {})}
        {...(googleSheetsUrl ? { googleSheetsUrl } : {})}
        {...(ccmApiUrl ? { ccmApiUrl } : {})}
        {...(leagueAppsConfigured ? { leagueAppsConfigured } : {})}
        {...(curlOpsConfigured ? { curlOpsConfigured } : {})}
      />
    </div>
  );
}

// ─── Blackout Dates Tab ───────────────────────────────────────────────────────
function BlackoutsTab({ facilityId }: { facilityId: string | null }) {
  const queryClient = useQueryClient();

  const [showForm, setShowForm] = useState(false);
  const [editBlackout, setEditBlackout] = useState<BlackoutDate | null>(null);
  const [form, setForm] = useState({ startDate: '', endDate: '', reason: '' });
  const [formError, setFormError] = useState<string | null>(null);

  const queryParams = facilityId ? { facilityId } : {};

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.schedules.blackouts(queryParams),
    queryFn: () => schedulesApi.listBlackouts(queryParams),
  });

  const createMutation = useMutation({
    mutationFn: (d: { facilityId?: string; startDate: string; endDate: string; reason?: string }) =>
      schedulesApi.createBlackout(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all });
      setShowForm(false);
      setForm({ startDate: '', endDate: '', reason: '' });
    },
    onError: (err: any) => setFormError(err?.message || 'Failed to save'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BlackoutDate> }) =>
      schedulesApi.updateBlackout(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all });
      setEditBlackout(null);
      setShowForm(false);
    },
    onError: (err: any) => setFormError(err?.message || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => schedulesApi.deleteBlackout(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.schedules.all }),
  });

  const openCreate = () => {
    setEditBlackout(null);
    setForm({ startDate: '', endDate: '', reason: '' });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (b: BlackoutDate) => {
    setEditBlackout(b);
    setForm({
      startDate: b.startDate.slice(0, 10),
      endDate: b.endDate.slice(0, 10),
      reason: b.reason || '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleSave = () => {
    setFormError(null);
    if (!form.startDate || !form.endDate) { setFormError('Start and end dates are required'); return; }
    const basePayload: { startDate: string; endDate: string; facilityId?: string; reason?: string } = {
      startDate: form.startDate,
      endDate: form.endDate,
    };
    if (facilityId) basePayload.facilityId = facilityId;
    if (form.reason) basePayload.reason = form.reason;
    if (editBlackout) {
      updateMutation.mutate({ id: editBlackout.id, data: basePayload });
    } else {
      createMutation.mutate(basePayload);
    }
  };

  const blackouts: BlackoutDate[] = data?.blackouts || [];
  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          Add Blackout
        </Button>
      </div>

      {/* Inline form */}
      {showForm && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{editBlackout ? 'Edit Blackout' : 'New Blackout Date'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {formError && (
              <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-sm font-medium">Start Date</label>
                <input
                  type="date"
                  value={form.startDate}
                  onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">End Date</label>
                <input
                  type="date"
                  value={form.endDate}
                  onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Reason</label>
              <input
                type="text"
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="e.g. Facility maintenance"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={isPending}>
                {isPending ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          Failed to load blackout dates
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Start</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">End</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Reason</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-400">Active</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {blackouts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                    No blackout dates
                  </td>
                </tr>
              ) : blackouts.map(b => (
                <tr key={b.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3">{b.startDate.slice(0, 10)}</td>
                  <td className="px-4 py-3">{b.endDate.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{b.reason || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={b.isActive ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}>
                      {b.isActive ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        title="Edit"
                        onClick={() => openEdit(b)}
                        className="rounded p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => { if (confirm('Delete this blackout?')) deleteMutation.mutate(b.id); }}
                        className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Agent Status Tab ─────────────────────────────────────────────────────────
// Agent host config is managed in Admin → Facilities → [Facility] → Agent tab.
// This tab shows live per-session status and logs.
function AgentConfigTab({ facilityId, hosts, recentLogs, isLoading }: {
  facilityId: string | null;
  hosts: AgentHostStatus[];
  recentLogs: any[];
  isLoading: boolean;
}) {
  const effectiveFacility = facilityId || '';

  // State → display config
  const stateStyle: Record<string, { label: string; cls: string }> = {
    IDLE:               { label: 'Idle',         cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
    FETCHING:           { label: 'Fetching',      cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    VALIDATING:         { label: 'Validating',    cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
    UPDATING_YOUTUBE:   { label: 'YouTube',       cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
    PREPARING_OBS:      { label: 'Prep OBS',      cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
    STARTING_STREAM:    { label: 'Starting',      cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300' },
    STREAMING:          { label: 'Streaming',     cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
    STOPPING:           { label: 'Stopping',      cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
    ERROR:              { label: 'Error',         cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' },
  };

  const hostStateDot: Record<string, string> = {
    READY:       'bg-green-500',
    DEGRADED:    'bg-amber-400',
    ERROR:       'bg-red-500',
    BOOTING:     'bg-blue-400',
    REGISTERING: 'bg-blue-400',
  };

  return (
    <div className="space-y-6">
      {!effectiveFacility ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Select a facility to view agent status.</p>
      ) : (
        <>
          {/* Config link */}
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm dark:border-blue-800/50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300">
            Agent host configuration (API keys, OBS addresses, sheet assignments) is managed in
            {' '}<strong>Admin → Facilities → [Facility] → Agent</strong> tab.
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : (
            <div className="space-y-6">
              {hosts.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">No agent hosts configured for this facility.</p>
              )}

              {hosts.map((h) => {
                const dotCls = h.isOnline
                  ? (hostStateDot[h.hostState ?? ''] ?? 'bg-green-500')
                  : 'bg-gray-400';
                const statusLabel = h.isOnline
                  ? (h.hostState ?? 'Online')
                  : h.lastHeartbeat
                    ? `Offline · last seen ${new Date(h.lastHeartbeat.seenAt).toLocaleTimeString()}`
                    : 'Never connected';

                const updateAvailable = h.agentVersion && h.latestAgentVersion && h.agentVersion !== h.latestAgentVersion;

                return (
                  <Card key={h.host.id}>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                        <span className={`relative flex h-2.5 w-2.5 shrink-0`}>
                          {h.isOnline && (
                            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${dotCls}`} />
                          )}
                          <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${dotCls}`} />
                        </span>
                        {h.host.hostLabel}
                        <span className="ml-1 text-sm font-normal text-gray-500 dark:text-gray-400">{statusLabel}</span>
                        {h.agentVersion && (
                          <span
                            className={`ml-auto rounded-full px-2 py-0.5 text-xs font-medium ${
                              updateAvailable
                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                            }`}
                            title={updateAvailable ? `Server has v${h.latestAgentVersion}; will auto-update at next safe window` : 'Running latest version'}
                          >
                            v{h.agentVersion}
                            {updateAvailable && <span className="ml-1">↑ v{h.latestAgentVersion}</span>}
                          </span>
                        )}
                      </CardTitle>
                      {/* NDI Discovery Server — only on the host that is its site's
                          discovery server (agent 1.0.37+ reports it). */}
                      {h.isOnline && h.ndiDiscovery?.expected && (() => {
                        const n = h.ndiDiscovery!;
                        const t = (iso: string | null) => iso ? new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
                        const cls = n.state === 'ok'
                          ? 'text-green-700 dark:text-green-400'
                          : n.state === 'faulted' ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400';
                        return (
                          <p className={`mt-1 text-xs ${cls}`}>
                            NDI Discovery Server:{' '}
                            {n.state === 'ok' ? 'running' : n.state === 'restarting' ? 'stopped — restarting' : n.state === 'faulted' ? 'down — could not restart' : n.state}
                            {n.restartsToday > 0 && (
                              <span className="text-gray-500 dark:text-gray-400">
                                {' '}· restarted {n.restartsToday} time{n.restartsToday === 1 ? '' : 's'} today (last {t(n.lastRestartAt)})
                              </span>
                            )}
                            {n.state === 'faulted' && n.lastError && <span className="text-gray-500 dark:text-gray-400"> · {n.lastError}</span>}
                          </p>
                        );
                      })()}
                    </CardHeader>
                    <CardContent>
                      {h.sessions.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No session data in last heartbeat.</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-100 dark:border-gray-700">
                                <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Sheet</th>
                                <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">State</th>
                                <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">OBS</th>
                                <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">YouTube</th>
                                <th className="pb-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Last Error</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                              {h.sessions.map((sess) => {
                                const sd = stateStyle[sess.state] ?? { label: sess.state, cls: 'bg-gray-100 text-gray-600' };
                                return (
                                  <tr key={sess.sheetName} className="hover:bg-gray-50 dark:hover:bg-gray-800/40">
                                    <td className="py-2 pr-4 font-medium text-gray-800 dark:text-gray-200">
                                      {sess.sheetName}
                                      {sess.isUnscheduled && (
                                        <span className="ml-1 text-xs text-gray-400">(unscheduled)</span>
                                      )}
                                      {sess.startedRemotelyBy && (
                                        <span
                                          title={sess.startedRemotelyBy === 'curlops' ? 'This stream was started from CurlOps' : 'This stream was started from the CSM website'}
                                          className="ml-1.5 inline-flex items-center rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                                        >
                                          {sess.startedRemotelyBy === 'curlops' ? 'CurlOps' : `web · ${sess.startedRemotelyBy === 'admin' ? 'Support' : 'Club Mgr'}`}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-2 pr-4">
                                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${sd.cls}`}>
                                        {sess.state === 'STREAMING' && (
                                          <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                                        )}
                                        {sd.label}
                                      </span>
                                    </td>
                                    <td className="py-2 pr-4">
                                      {sess.obsConnected
                                        ? <Wifi className="h-4 w-4 text-green-500" />
                                        : <WifiOff className="h-4 w-4 text-red-400" />}
                                    </td>
                                    <td className="py-2 pr-4">
                                      {sess.broadcastId ? (
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                          <Youtube className="h-3.5 w-3.5 text-red-500" />
                                          <span className={
                                            sess.broadcastStatus === 'active' ? 'text-green-600 dark:text-green-400 font-medium' :
                                            sess.broadcastStatus === 'complete' ? 'text-gray-400' :
                                            'text-gray-500'
                                          }>
                                            {sess.broadcastStatus ?? 'unknown'}
                                          </span>
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                                      )}
                                    </td>
                                    <td className="py-2 max-w-xs">
                                      {sess.lastError ? (
                                        <span className="truncate text-xs text-red-600 dark:text-red-400" title={sess.lastError}>
                                          {sess.lastError}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-300 dark:text-gray-600">—</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}

              {/* Recent logs */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Radio className="h-4 w-4 text-gray-400" />
                    Recent Agent Logs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72 overflow-y-auto rounded bg-gray-50 p-2 text-xs font-mono dark:bg-gray-900">
                    {recentLogs.length === 0 ? (
                      <span className="text-gray-400">No log entries</span>
                    ) : (
                      recentLogs.slice(0, 100).map((log: any, i: number) => (
                        <div key={i} className={`mb-0.5 ${
                          log.level === 'error' ? 'text-red-500' :
                          log.level === 'warn' ? 'text-yellow-500' :
                          'text-gray-700 dark:text-gray-300'
                        }`}>
                          <span className="text-gray-400 select-none">
                            {log.createdAt ? new Date(log.createdAt).toLocaleTimeString() : ''}
                          </span>{' '}
                          <span className="text-gray-500 dark:text-gray-500">[{log.level?.toUpperCase() || 'INFO'}]</span>{' '}
                          {log.event && <span className="text-blue-500 dark:text-blue-400">{log.event} </span>}
                          {log.message || JSON.stringify(log)}
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
type Tab = 'schedules' | 'events' | 'adhoc' | 'live' | 'archive' | 'blackouts' | 'agent';
const TABS: readonly Tab[] = ['schedules', 'events', 'adhoc', 'live', 'archive', 'blackouts', 'agent'];

export default function SchedulesPage() {
  // The tab lives in the address (?tab=events), so a refresh, the back button or
  // a shared link lands on the same tab instead of always on Scheduled Streams.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as Tab | null;
  const activeTab: Tab = tabParam && TABS.includes(tabParam) ? tabParam : 'schedules';
  const setActiveTab = (t: Tab) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    next.set('tab', t);
    return next;
  }, { replace: true });

  const facilityId = useActiveFacilityId() ?? null;

  // Facility list — used to derive the current facility's timezone
  const { data: facilitiesData } = useQuery({
    queryKey: ['facilities-list'],
    queryFn: () => facilitiesApi.list(),
    staleTime: 5 * 60 * 1000,
  });
  const activeFacility = facilitiesData?.facilities?.find(f => f.facilityId === facilityId);
  const scheduleWindowDays = facilitiesData?.scheduleWindowDays;
  const lastSyncIssues = activeFacility?.lastSyncIssues ?? null;
  const facilityTimezone = activeFacility?.timezone;
  const scheduleSource = activeFacility?.scheduleSource;
  const googleSheetsUrl = activeFacility?.googleSheetsUrl;
  const ccmApiUrl = activeFacility?.ccmApiUrl;
  // Prefer the server's non-secret flag: the API keys these used to be derived
  // from are stripped for club managers, which left them with no sync path.
  const leagueAppsConfigured = activeFacility?.leagueAppsConfigured
    ?? !!(activeFacility?.leagueAppsApiKey && activeFacility?.leagueAppsSiteId);
  // curlOpsApiKey is sanitized out for facility users, so gate on the (non-secret)
  // slug — the server holds the key and does the fetch.
  const curlOpsConfigured = activeFacility?.curlOpsConfigured ?? !!activeFacility?.curlOpsSlug;
  const streamingPaused = !!activeFacility?.streamingPaused;
  const lastSheetsSync = activeFacility?.lastSheetsSync;
  const broadcastModeUntil = activeFacility?.broadcastModeUntil ?? null;
  const broadcastModeActive = !!broadcastModeUntil && new Date(broadcastModeUntil) > new Date();

  // Lifted here so both SchedulesTab (live indicator) and AgentConfigTab share it
  const { data: statusData, isLoading: agentLoading } = useQuery({
    queryKey: queryKeys.schedules.agentStatus(facilityId ?? ''),
    queryFn: () => schedulesApi.getAgentStatus(facilityId ?? ''),
    enabled: !!facilityId,
    refetchInterval: 30_000,
  });

  const agentHosts: AgentHostStatus[] = (statusData?.hosts ?? []) as unknown as AgentHostStatus[];
  const recentLogs = (statusData?.recentLogs ?? []) as any[];

  // Build sheetId → AgentSessionStatus map from all hosts' latest heartbeat sessions
  const liveMap = new Map<string, AgentSessionStatus>();
  for (const h of agentHosts) {
    for (const sess of h.sessions) {
      const letter = sheetLetterFromName((sess as AgentSessionStatus).sheetName);
      liveMap.set(letter, sess as AgentSessionStatus);
    }
  }

  // Surface an active-event indicator on the Events tab label.
  const { data: eventsData } = useQuery({
    queryKey: queryKeys.events.list(facilityId ?? ''),
    queryFn: () => eventsApi.list(facilityId ?? ''),
    enabled: !!facilityId,
    refetchInterval: 60_000,
  });
  const activeEvent = (eventsData?.events ?? []).find(e => e.isInEventMode);
  const eventModeActive = !!activeEvent;

  const tabs: { key: Tab; label: string }[] = [
    { key: 'schedules', label: 'Scheduled Streams' },
    { key: 'events', label: eventModeActive ? 'Event Streams ●' : 'Event Streams' },
    { key: 'adhoc', label: 'Ad Hoc Streams' },
    { key: 'live', label: 'Live Now' },
    { key: 'archive', label: 'Archive' },
    { key: 'blackouts', label: 'Blackout Dates' },
    { key: 'agent', label: 'Agent Status' },
  ];

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Streaming</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Scheduled, event, and ad-hoc streams, plus live streams, blackout dates, and agent status.
        </p>
        {facilityTimezone && (
          <p className="mt-1 text-sm font-medium text-blue-600 dark:text-blue-400">
            Times shown are in {facilityTimezone} (facility local time).
          </p>
        )}
      </div>

      {/* Broadcast Mode banner — an admin has taken over streaming for this facility.
          Shown on every sub-tab so club managers understand why start/stop is off. */}
      {broadcastModeActive && (
        <div className="flex items-start gap-3 rounded-lg bg-red-700 px-5 py-4 text-white shadow-md">
          <AlertTriangle className="mt-0.5 h-7 w-7 shrink-0 text-white" />
          <div className="min-w-0">
            <p className="text-lg font-bold text-white">
              BROADCAST MODE IS ON — a Stadium Sports administrator has taken over streaming for this facility.
            </p>
            <p className="mt-1 text-sm text-white">
              Automatic and remote start/stop are disabled until {fmtInTz(broadcastModeUntil!, facilityTimezone)} ({timeRemaining(broadcastModeUntil)}). You can still view and edit schedules — changes are saved and take effect once Broadcast Mode ends.
            </p>
          </div>
        </div>
      )}

      {/* Event Mode banner — the league schedule isn't auto-starting, which is easy to
          miss from the other tabs. The Event Streams tab has its own banner. */}
      {activeEvent && activeTab !== 'events' && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-purple-700 px-5 py-4 text-white shadow-md">
          <AlertTriangle className="h-7 w-7 shrink-0 text-white" />
          <div className="min-w-0 flex-1">
            <p className="text-lg font-bold text-white">
              EVENT MODE IS ON — {activeEvent.name}
            </p>
            <p className="mt-1 text-sm text-white">
              Scheduled league games won't start automatically until {fmtInTz(activeEvent.eventModeUntil!, facilityTimezone)} ({timeRemaining(activeEvent.eventModeUntil)}).
            </p>
          </div>
          <button
            onClick={() => setActiveTab('events')}
            className="shrink-0 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-purple-800 hover:bg-purple-100"
          >
            Go to Event Streams
          </button>
        </div>
      )}

      {/* Tab Switcher */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
                activeTab === t.key
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'schedules' && (
        <SchedulesTab
          facilityId={facilityId}
          liveMap={liveMap}
          {...(facilityTimezone ? { facilityTimezone } : {})}
          {...(scheduleSource ? { scheduleSource } : {})}
          {...(googleSheetsUrl ? { googleSheetsUrl } : {})}
          {...(ccmApiUrl ? { ccmApiUrl } : {})}
          {...(leagueAppsConfigured ? { leagueAppsConfigured } : {})}
          {...(curlOpsConfigured ? { curlOpsConfigured } : {})}
          streamingPaused={streamingPaused}
          {...(lastSheetsSync ? { lastSheetsSync } : {})}
          {...(scheduleWindowDays ? { scheduleWindowDays } : {})}
          {...(lastSyncIssues ? { lastSyncIssues } : {})}
          {...(activeFacility?.sheetNames ? { sheetNames: activeFacility.sheetNames } : {})}
          {...(activeFacility?.pausedSheets ? { pausedSheets: activeFacility.pausedSheets } : {})}
        />
      )}
      {activeTab === 'events' && (
        <EventModePanel facilityId={facilityId} broadcastModeActive={broadcastModeActive} curlOps={scheduleSource === 'curlops_api' && !!curlOpsConfigured} {...(facilityTimezone ? { facilityTimezone } : {})} />
      )}
      {activeTab === 'adhoc' && (
        <AdHocStreamsTab facilityId={facilityId} hosts={agentHosts} isLoading={agentLoading} broadcastModeActive={broadcastModeActive} {...(facilityTimezone ? { facilityTimezone } : {})} />
      )}
      {activeTab === 'live' && <LiveStreamsTab facilityId={facilityId} />}
      {activeTab === 'archive' && (
        <SchedulesTab
          archive
          facilityId={facilityId}
          liveMap={liveMap}
          {...(facilityTimezone ? { facilityTimezone } : {})}
          {...(scheduleSource ? { scheduleSource } : {})}
          streamingPaused={streamingPaused}
        />
      )}
      {activeTab === 'blackouts' && <BlackoutsTab facilityId={facilityId} />}
      {activeTab === 'agent' && (
        <AgentConfigTab
          facilityId={facilityId}
          hosts={agentHosts}
          recentLogs={recentLogs}
          isLoading={agentLoading}
        />
      )}
    </div>
  );
}
